import React, { useEffect } from "react";
import { useMetaPlatform } from "../../hooks/useMetaGraphVersion";
import { loadMetaSdk, loginWithMetaApp } from "../../helpers/metaSdk";
import { toast } from "../../services/toast";
import { i18n } from "../../translate/i18n";
import { makeStyles } from "tss-react/mui";
import { Box, IconButton, Modal, Paper, Typography } from "@mui/material";
import {
  Close,
  Facebook,
  Instagram,
  WhatsApp,
  ChatBubbleOutlineOutlined,
  RecordVoiceOverOutlined,
  PhoneInTalkOutlined,
  MusicNote,
  ScienceOutlined,
} from "@mui/icons-material";


const SCOPE_FACEBOOK =
  "public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management";
const SCOPE_INSTAGRAM =
  "public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management";
const SCOPE_WHATSAPP = "whatsapp_business_messaging,whatsapp_business_management";

const CANALES = [
  {
    clave: "whatsapp",
    titulo: "WhatsApp QR",
    desc: "Escaneas un código y listo. No es la API oficial de Meta.",
    icono: <WhatsApp />,
    color: "#25d366",
    via: "local",
  },
  {
    clave: "whatsapp_cloud",
    titulo: "WhatsApp API Cloud",
    desc: "La API oficial de Meta. Ventana de 24h y plantillas aprobadas.",
    icono: <WhatsApp />,
    color: "#25d3c1",
    via: "meta",
    metaApp: "whatsappAppId",
    scope: SCOPE_WHATSAPP,
  },
  {
    clave: "facebook",
    titulo: "Facebook",
    desc: "Los mensajes de tu página, aquí dentro.",
    icono: <Facebook />,
    color: "#3b5998",
    via: "meta",
    metaApp: "appId",
    scope: SCOPE_FACEBOOK,
  },
  {
    clave: "instagram",
    titulo: "Instagram",
    desc: "Los DMs de tu cuenta profesional.",
    icono: <Instagram />,
    color: "#e1306c",
    via: "meta",
    metaApp: "appId",
    scope: SCOPE_INSTAGRAM,
  },

  {
    clave: "whatsapp_coexistencia",
    titulo: "WhatsApp API Coexistencia",
    desc: "La app del móvil y la API oficial sobre el mismo número, a la vez.",
    icono: <WhatsApp />,
    color: "#25d366",
    via: "embedded",
  },

  {
    clave: "tiktok",
    titulo: "TikTok",
    desc: "Mensajes y contactos de tu cuenta TikTok for Business.",
    icono: <MusicNote />,
    color: "#ee1d52",
    via: "ninguna",
  },
  {
    clave: "webchat",
    titulo: "Web Chat",
    desc: "Un chat en tu web, para captar y atender ahí mismo.",
    icono: <ChatBubbleOutlineOutlined />,
    color: "#5b6cff",
    via: "directo",
  },
  {
    clave: "sandbox",
    titulo: "Sandbox",
    desc: "Un cliente de mentira para probar departamentos, bots y avisos.",
    icono: <ScienceOutlined />,
    color: "#8b8f9a",
    via: "directo",
  },
  {
    clave: "avatar",
    titulo: "Avatar",
    desc: "Un avatar de voz (OpenAI Realtime) para atender llamadas en tu web.",
    icono: <RecordVoiceOverOutlined />,
    color: "#8a5cf6",
    via: "directo",
  },
  {
    clave: "sip",
    titulo: "Llamadas",
    desc: "Un número de teléfono real (SIP), atendido por la misma IA.",
    icono: <PhoneInTalkOutlined />,
    color: "#2fae60",
    via: "directo",
  },
];

const useStyles = makeStyles()((theme) => ({
  caja: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 720,
    maxWidth: "94vw",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    outline: "none",
    overflow: "hidden",
  },
  cabecera: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(2.5, 3, 1.5),
  },
  titulo: { fontSize: 16, fontWeight: 600, lineHeight: 1.2, flex: 1 },
  cuerpo: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0, 2, 2.5),
    overflowY: "auto",
    minHeight: 0,
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },
  tarjeta: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.25),
    borderRadius: 8,
    border: "none",
    textAlign: "left",
    width: "100%",
    background: "transparent",
    font: "inherit",
    color: "inherit",
    cursor: "pointer",
    transition: "background 0.15s ease",
    "&:hover": { background: theme.palette.action.hover },
  },
  tarjetaApagada: {
    opacity: 0.38,
    cursor: "default",
    "&:hover": { background: "transparent" },
  },
  icono: {
    display: "flex",
    flexShrink: 0,
    marginTop: 1,
    "& svg": { fontSize: 20 },
  },
  nombre: { fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 },
  desc: { fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.35 },
}));

const Tarjeta = ({ canal, classes, disponible, motivo, onClick }) => {
  const contenido = (
    <>
      <Box className={classes.icono} style={{ color: canal.color }}>
        {canal.icono}
      </Box>
      <Box style={{ minWidth: 0 }}>
        <Typography className={classes.nombre}>{canal.titulo}</Typography>
        <Typography className={classes.desc}>{canal.desc}</Typography>
      </Box>
    </>
  );

  if (!disponible) {
    return (
      <Box
        className={`${classes.tarjeta} ${classes.tarjetaApagada}`}
        aria-disabled="true"
        title={motivo || "No disponible en tu instalación"}
      >
        {contenido}
      </Box>
    );
  }

  return (
    <button type="button" className={classes.tarjeta} onClick={onClick}>
      {contenido}
    </button>
  );
};

const ConnectionPicker = ({
  open,
  onClose,
  onWhatsApp,
  onFacebook,
  onInstagram,
  onWhatsAppCloud,
  onCoexistencia,
  onWebChat, onSandbox, onAvatar, onSip,
  channelAvailability = {},
}) => {
  const { classes } = useStyles();
  const metaPlatform = useMetaPlatform();

  useEffect(() => {
    if (open) loadMetaSdk().catch(() => {});
  }, [open]);

  const connectMeta = async (canal) => {
    try {
      const response = await loginWithMetaApp({
        appId: metaPlatform[canal.metaApp],
        version: metaPlatform.version,
        scope: canal.scope,
      });
      respuestaMeta[canal.clave](response);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const respuestaMeta = {
    whatsapp_cloud: onWhatsAppCloud,
    facebook: onFacebook,
    instagram: onInstagram,
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="picker-title">
      <Paper className={classes.caja}>
        <Box className={classes.cabecera}>
          <Typography className={classes.titulo} id="picker-title">
            Agregar conexión
          </Typography>

          <IconButton size="small" onClick={onClose} aria-label="Cerrar">
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box className={classes.cuerpo}>
          {CANALES.map((canal) => {
            if (canal.via === "ninguna") {
              return (
                <Tarjeta key={canal.clave} canal={canal} classes={classes} disponible={false} />
              );
            }

            if (canal.via === "directo") {
              const ONCLICK_DIRECTO = {
                sandbox: onSandbox,
                webchat: onWebChat,
                avatar: onAvatar,
                sip: onSip,
              };

              const disponible = channelAvailability[canal.clave] !== false;

              return (
                <Tarjeta
                  key={canal.clave}
                  canal={canal}
                  classes={classes}
                  disponible={disponible}
                  motivo={!disponible ? "No incluido en tu plan actual" : undefined}
                  onClick={ONCLICK_DIRECTO[canal.clave]}
                />
              );
            }

            if (canal.via === "embedded") {
              return (
                <Tarjeta
                  key={canal.clave}
                  canal={canal}
                  classes={classes}
                  disponible
                  onClick={onCoexistencia}
                />
              );
            }

            if (canal.via === "local") {
              return (
                <Tarjeta
                  key={canal.clave}
                  canal={canal}
                  classes={classes}
                  disponible
                  onClick={() => {
                    onClose();
                    onWhatsApp();
                  }}
                />
              );
            }

            if (!metaPlatform[canal.metaApp]) {
              return <Tarjeta key={canal.clave} canal={canal} classes={classes} motivo={i18n.t("metaGraphVersion.missingApp")} />;
            }

            return (
              <Tarjeta key={canal.clave} canal={canal} classes={classes} disponible onClick={() => connectMeta(canal)} />
            );
          })}
        </Box>
      </Paper>
    </Modal>
  );
};

export default ConnectionPicker;
